import React, { useState, useRef, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { NavLink, Link } from "react-router-dom";
import { useAuth } from "../../provider/authProvider";
import Nav from "./Appbar";
import ComingSoon from "../../Pages/ComingSoon/ComingSoon";
import Products from "../../Components/Products/Products";
import logo from "../../assets/logo.png";

const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact Us" },
  { to: "/ambassador", label: "Campus" },
  { to: "/products", label: "Products", hasPreview: true },
];

const navLinkClassName = ({ isActive }) =>
  [
    "whitespace-nowrap font-serif text-[15px] font-bold leading-[1.2] tracking-[0.01em] text-black transition-colors duration-200",
    "hover:text-[var(--primarty-text-color)]",
    isActive ? "text-[var(--primarty-text-color)]" : "",
  ]
    .filter(Boolean)
    .join(" ");

const Navbar = () => {
  const [toggle, setToggle] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isProductsHovered, setIsProductsHovered] = useState(false);
  const [isDesktopNav, setIsDesktopNav] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1026 : false
  );
  const contentToggleRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const productsPreviewTimeoutRef = useRef(null);
  const { isLoggedIn } = useAuth();

  const toggleBtn = () => {
    setToggle(!toggle);
  };

  // Function to handle clicks outside the popup
  const handleClickOutside = (event) => {
    if (
      (contentToggleRef.current &&
        contentToggleRef.current.contains(event.target)) ||
      (toggleBtnRef.current && toggleBtnRef.current.contains(event.target))
    ) {
      return;
    }
    if (event.target.closest("#menu-appbar")) {
      return;
    }
    setToggle(true);
    setIsProductsHovered(false);
  };

  const handleNavLinkClick = () => {
    setToggle(true); // Set the toggle to true to hide the popup
    setIsProductsHovered(false);
  };

  const clearProductsPreviewTimeout = () => {
    if (productsPreviewTimeoutRef.current) {
      clearTimeout(productsPreviewTimeoutRef.current);
      productsPreviewTimeoutRef.current = null;
    }
  };

  const openProductsPreview = () => {
    if (!isDesktopNav) {
      return;
    }

    clearProductsPreviewTimeout();
    setIsProductsHovered(true);
  };

  const closeProductsPreview = () => {
    clearProductsPreviewTimeout();
    productsPreviewTimeoutRef.current = setTimeout(() => {
      setIsProductsHovered(false);
    }, 120);
  };

  const navContentClassName = toggle
    ? "hidden min-w-0 flex-1 items-center justify-between pr-16"
    : "fixed left-0 top-[var(--navbar-height)] z-[1000] flex w-max max-w-[calc(100vw-1rem)] flex-col items-start gap-4 rounded-br-xl bg-[#F7F8FF] px-4 py-4 shadow-[0_12px_30px_rgba(65,48,224,0.14)]";

  const navLinksWrapperClassName = toggle
    ? "ml-5 flex min-w-0 flex-1 items-center justify-start gap-4 lg:ml-7 lg:gap-6 xl:ml-10 xl:gap-8"
    : "flex flex-col items-start gap-4 pb-1";

  const navActionsClassName = toggle
    ? "ml-3 flex shrink-0 items-center gap-2 lg:ml-4 lg:gap-3 xl:gap-5"
    : "flex flex-col items-stretch gap-3 pb-1";

  useEffect(() => {
    // Add event listener for clicks outside
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearProductsPreviewTimeout();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const desktopLayout = window.innerWidth >= 1026;
      setIsDesktopNav(desktopLayout);
      if (desktopLayout) {
        setToggle(true);
      } else {
        setIsProductsHovered(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <div className="fixed top-0 z-[1000] flex w-full items-center bg-[#F7F8FF] px-3 shadow-[0_2px_5px_rgba(65,48,224,0.08)]">
        <div className="flex h-[var(--navbar-height)] shrink-0 items-center gap-5 pl-1 sm:pl-8 md:pl-12 lg:pl-16">
          <div className="pb-[5px] pr-3 text-[30px] font-bold lg:pr-[30px]">
            <Link to={"/individual"} className="flex items-center">
              <img
                src={logo}
                alt="logo"
                loading="lazy"
                decoding="async"
                className="h-[30px] w-auto"
              />
              <span style={{ color: "black" }}>Code</span>
              <span style={{ color: "var(--primarty-text-color)" }}>Queue</span>
            </Link>
          </div>
        </div>

        <div
          ref={contentToggleRef}
          className={navContentClassName}
          style={{ display: isDesktopNav ? "flex" : toggle ? "none" : "flex" }}
        >
          <div className={navLinksWrapperClassName}>
            {navLinks.map((link) => {
              if (link.hasPreview && isDesktopNav) {
                return (
                  <div
                    key={link.to}
                    className="relative flex items-center self-stretch"
                    onMouseEnter={openProductsPreview}
                    onMouseLeave={closeProductsPreview}
                  >
                    <NavLink
                      to={link.to}
                      end={link.end}
                      className={({ isActive }) =>
                        [
                          navLinkClassName({ isActive }),
                          "inline-flex h-full items-center gap-1",
                        ].join(" ")
                      }
                      onClick={handleNavLinkClick}
                    >
                      <span>{link.label}</span>
                      <KeyboardArrowDownRoundedIcon
                        style={{ fontSize: "1.1rem", marginTop: "1px" }}
                      />
                    </NavLink>

                    {isProductsHovered && (
                      <div className="absolute left-1/2 top-full z-[1100] mt-4 w-[min(88vw,860px)] -translate-x-1/2">
                        <div
                            className="rounded-[32px]"
                            onMouseEnter={openProductsPreview}
                            onMouseLeave={closeProductsPreview}
                          >
                            <Products embedded onNavigate={handleNavLinkClick} />
                          </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={navLinkClassName}
                  onClick={handleNavLinkClick}
                >
                  {link.label}
                </NavLink>
              );
            })}
          </div>

          {isLoggedIn() ? (
            <div className={navActionsClassName}>
              <Nav onMenuAction={() => setToggle(true)} />
            </div>
          ) : (
            <div className={navActionsClassName}>
              <NavLink
                to={"/login"}
                // className="flex min-w-0 items-center justify-center whitespace-nowrap rounded-[5px] border border-[#ccc] px-3 pb-[10px] pt-[7px] text-sm font-bold text-black transition-colors duration-200 hover:bg-[#d6d6d6] lg:px-[15px]"
                className="flex min-w-0 items-center justify-center whitespace-nowrap rounded-[5px] bg-[var(--primarty-text-color)] px-3 pb-[10px] pt-[7px] text-sm font-bold text-white transition-colors duration-200 hover:bg-[rgb(13,13,145)] lg:px-[15px]"
                onClick={handleNavLinkClick}
              >
                Sign in
              </NavLink>
              {/* <NavLink
                to={"/signup"}
                className="flex min-w-0 items-center justify-center whitespace-nowrap rounded-[5px] bg-[var(--primarty-text-color)] px-3 pb-[10px] pt-[7px] text-sm font-bold text-white transition-colors duration-200 hover:bg-[rgb(13,13,145)] lg:px-[15px]"
                onClick={handleNavLinkClick}
              >
                Sign up free
              </NavLink> */}
            </div>
          )}
        </div>

        <div
          ref={toggleBtnRef}
          className="absolute right-5 flex h-16 items-center"
          style={{ display: isDesktopNav ? "none" : "flex" }}
        >
          <MenuIcon onClick={toggleBtn} />
        </div>
      </div>
      <ComingSoon isVisible={isVisible} setIsVisible={setIsVisible} />
    </>
  );
};

export default Navbar;
